import { formatDistanceStrict } from 'date-fns';
import { recommendChannel } from '@/domain/channelRecommendation';
import { aud, formatBodyPart, simDateShort } from '@/utils/format';
import { continuablePiecesFor, pieceTitleOf } from '@/utils/pieceHelpers';
import { depositsForCustomer } from '@/utils/depositHelpers';
import type {
  Booking,
  Channel,
  ConversationEntry,
  CustomerRecord,
  Deposit,
  Piece,
} from '@/domain/types';

/** How long the verbatim quote runs before it gets clipped (matches the feed). */
const QUOTE_CLIP = 150;

const CHANNEL_LABEL: Record<Channel, string> = {
  'meta-dm': 'Meta DM',
  'ig-dm': 'Instagram DM',
  sms: 'SMS',
  voice: 'call',
  'web-form': 'web form',
  'walk-in': 'walk-in',
  email: 'email',
};

/** One labelled fact on the card. `tone` drives emphasis, not decoration. */
export interface GlanceRow {
  key: string;
  label: string;
  value: string;
  tone?: 'urgent' | 'good';
}

export interface Glance {
  rows: GlanceRow[];
  /** The client's own last words, clipped. Absent when they have never written in. */
  quote?: { body: string; attribution: string };
  /** Total messages on file, so the disclosure can say what it is hiding. */
  messageCount: number;
}

function clip(body: string): string {
  const flat = body.replace(/\s+/g, ' ').trim();
  return flat.length > QUOTE_CLIP ? `${flat.slice(0, QUOTE_CLIP - 1)}…` : flat;
}

/** "3 days", "14 months" - a gap, not a date, so staleness reads at a glance. */
function gap(fromIso: string, nowIso: string): string {
  return formatDistanceStrict(new Date(fromIso), new Date(nowIso));
}

/** True when the timestamp has not happened yet on the simulation clock. */
function isFuture(iso: string, nowIso: string): boolean {
  return new Date(iso).getTime() > new Date(nowIso).getTime();
}

/**
 * Direction-aware gap. formatDistanceStrict returns an absolute distance, so a
 * future-dated entry would otherwise read as "55 minutes ago" - and the seed does
 * contain entries stamped ahead of the clock.
 */
function relativeGap(fromIso: string, nowIso: string): string {
  const dist = gap(fromIso, nowIso);
  return isFuture(fromIso, nowIso) ? `in ${dist}` : `${dist} ago`;
}

function lastEntry(conversation: ConversationEntry[]): ConversationEntry | undefined {
  return conversation.reduce<ConversationEntry | undefined>(
    (latest, e) => (!latest || e.timestamp > latest.timestamp ? e : latest),
    undefined,
  );
}

function lastInbound(conversation: ConversationEntry[]): ConversationEntry | undefined {
  return lastEntry(conversation.filter((e) => e.direction === 'in'));
}

/** The deposit worth mentioning: unsettled first, then the most recent. */
function headlineDeposit(deposits: Deposit[]): Deposit | undefined {
  const rank = (d: Deposit) =>
    d.status === 'expired' ? 0 : d.status === 'pending' ? 1 : isPartlyPaid(d) ? 2 : 3;
  return [...deposits].sort(
    (a, b) => rank(a) - rank(b) || (a.createdAt < b.createdAt ? 1 : -1),
  )[0];
}

function isPartlyPaid(d: Deposit): boolean {
  return d.status === 'paid' && d.amountPaid != null && d.amountPaid < d.amountAud;
}

function depositRow(d: Deposit, nowIso: string): GlanceRow {
  const paid = d.amountPaid ?? (d.status === 'paid' ? d.amountAud : 0);
  const part = paid > 0 && paid < d.amountAud ? `${aud(paid)} of ${aud(d.amountAud)} paid` : null;

  if (d.status === 'expired') {
    return {
      key: 'money',
      label: 'Money',
      value: `${aud(d.amountAud)} link expired ${simDateShort(d.expiresAt)}`,
      tone: 'urgent',
    };
  }
  if (d.status === 'pending') {
    const expired = new Date(d.expiresAt).getTime() <= new Date(nowIso).getTime();
    const timing = expired
      ? `expired ${simDateShort(d.expiresAt)}`
      : `expires in ${gap(d.expiresAt, nowIso)}`;
    return {
      key: 'money',
      label: 'Money',
      value: `${part ?? `${aud(d.amountAud)} link sent`} · ${timing}`,
      tone: 'urgent',
    };
  }
  if (part) {
    return { key: 'money', label: 'Money', value: `${part} · top-up due`, tone: 'urgent' };
  }
  return {
    key: 'money',
    label: 'Money',
    value: `${aud(d.amountAud)} deposit paid`,
    tone: 'good',
  };
}

/** What the client asked for, from the structured brief. */
function wantsLine(customer: CustomerRecord, continuing?: Piece): string | undefined {
  if (continuing) return `${pieceTitleOf(continuing)} · continuing`;
  const di = customer.designIntent;
  if (!di) return undefined;
  const parts = [
    di.type?.replace(/-/g, ' '),
    di.size === 'half-day' ? 'half day' : di.size === 'full-day' ? 'full day' : di.size,
    formatBodyPart({
      bodyPart: di.bodyPart,
      bodyPartSide: di.bodyPartSide,
      bodyPartQualifier: di.bodyPartQualifier,
      placement: di.placement,
    }).toLowerCase(),
  ].filter((p): p is string => Boolean(p && p.length > 0));
  return parts.length > 0 ? parts.join(' · ') : undefined;
}

/**
 * Everything an agent needs before they dial, as six facts rather than a
 * transcript. Every line is a lookup over data already on the record - there is
 * no inference here and no model call. `nowIso` is the simulation clock.
 */
export function buildGlance(args: {
  customer: CustomerRecord;
  bookings: Record<string, Booking>;
  deposits: Record<string, Deposit>;
  pieces: Record<string, Piece>;
  nowIso: string;
}): Glance {
  const { customer, bookings, deposits, pieces, nowIso } = args;
  const rows: GlanceRow[] = [];

  const di = customer.designIntent;
  const continuing =
    di?.pieceMode === 'continue' && di.continuePieceId ? pieces[di.continuePieceId] : undefined;

  const wants = wantsLine(customer, continuing);
  if (wants) rows.push({ key: 'wants', label: 'Wants', value: wants });

  const latest = lastEntry(customer.conversation);
  if (latest) {
    const direction = latest.direction === 'in' ? 'from them' : 'out';
    rows.push({
      key: 'last-contact',
      label: 'Last contact',
      value: `${relativeGap(latest.timestamp, nowIso)} · ${CHANNEL_LABEL[latest.channel]} ${direction}`,
    });
  } else {
    rows.push({ key: 'last-contact', label: 'Last contact', value: 'Never contacted' });
  }

  // Called out separately: an outbound-only thread looks busy while the client
  // has actually been silent for a year.
  const inbound = lastInbound(customer.conversation);
  if (inbound && inbound.id !== latest?.id) {
    rows.push({
      key: 'last-reply',
      label: 'Last reply',
      value: isFuture(inbound.timestamp, nowIso)
        ? `${CHANNEL_LABEL[inbound.channel]}, ${relativeGap(inbound.timestamp, nowIso)}`
        : `${CHANNEL_LABEL[inbound.channel]}, ${gap(inbound.timestamp, nowIso)} quiet`,
    });
  } else if (!inbound) {
    rows.push({ key: 'last-reply', label: 'Last reply', value: 'They have never replied' });
  }

  const money = headlineDeposit(depositsForCustomer(deposits, customer));
  if (money) rows.push(depositRow(money, nowIso));

  const rec = recommendChannel(customer);
  rows.push({
    key: 'channel',
    label: 'Best channel',
    value: `${CHANNEL_LABEL[rec.channel]} - ${rec.reason}`,
  });

  const completed = customer.bookings
    .map((id) => bookings[id])
    .filter((b): b is Booking => Boolean(b))
    .filter((b) => b.status === 'completed');
  const unfinished = continuablePiecesFor(pieces, customer).length;
  const history = [
    completed.length > 0
      ? `${completed.length} ${completed.length === 1 ? 'sitting' : 'sittings'}`
      : 'No sittings yet',
    customer.lifetimeSpendAud > 0 ? `${aud(customer.lifetimeSpendAud)} lifetime` : null,
    unfinished > 0 ? `${unfinished} unfinished` : null,
  ].filter((p): p is string => Boolean(p));
  rows.push({ key: 'history', label: 'History', value: history.join(' · ') });

  const quote = inbound
    ? {
        body: clip(inbound.body),
        attribution: `${customer.firstName} · ${CHANNEL_LABEL[inbound.channel]} · ${simDateShort(inbound.timestamp)}`,
      }
    : undefined;

  return { rows, quote, messageCount: customer.conversation.length };
}
